import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import EmblaCarousel from 'embla-carousel';
import PreviewItem from './PreviewItem';

/**
 * AccessoriesSlider
 * Groups accessories into slides of 4 (2 x 2 grid). Uses Embla only if more than one slide.
 */
const AccessoriesSlider = ({ accessories, accessoryInfo, dispatch, onEdit, onRemove, skintone = '' }) => {
    const viewportRef = useRef(null);
    const emblaRef = useRef(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [snapCount, setSnapCount] = useState(0);

    const AddAccessoryButton = () => (
        <button
            type="button"
            className="preview-accessories__addButton"
            aria-label="Add accessory"
            onClick={() => onEdit('accessories')}
        >
            <img src="https://cdn.shopify.com/s/files/1/0223/5446/0736/files/icon-plus.svg?v=1759745533" alt="Add" />
        </button>
    );

    // Chunk accessories into groups of 4 (2x2 grid per slide) and pad empty slots with null placeholders
    const slides = React.useMemo(() => {
        const out = [];
        for (let i = 0; i < accessories.length; i += 4) {
            const group = accessories.slice(i, i + 4);
            while (group.length < 4) group.push(null); // pad
            out.push(group);
        }
        // If there were no accessories, create a single slide of 4 placeholders
        if (out.length === 0) {
            out.push([null, null, null, null]);
        }
        return out;
    }, [accessories]);

    const setupEmbla = useCallback(() => {
        if (!viewportRef.current || slides.length <= 1) return;
        if (emblaRef.current) {
            emblaRef.current.destroy();
            emblaRef.current = null;
        }
        emblaRef.current = EmblaCarousel(viewportRef.current, {
            slidesToScroll: 1,
            containScroll: 'trimSnaps',
            align: 'start',
            dragFree: false,
        });
        const embla = emblaRef.current;
        const onSelect = () => setSelectedIndex(embla.selectedScrollSnap());
        embla.on('select', onSelect);
        setSnapCount(embla.scrollSnapList().length);
        onSelect();
    }, [slides.length]);

    useEffect(() => {
        setupEmbla();
        return () => {
            if (emblaRef.current) {
                emblaRef.current.destroy();
                emblaRef.current = null;
            }
        };
    }, [setupEmbla]);

    // Single slide: no carousel
    if (slides.length === 1) {
        return (
            <div className="preview-accessories preview-accessories--grid">
                <h3 className="vh">Accessories</h3>
                <div className="preview-accessories__grid">
                    {slides[0].map((acc, idx) => {
                        const isPlaceholder = !acc;
                        return (
                            <div className="preview-accessories__gridItem" key={`accessory-grid-${idx}`}>
                                {isPlaceholder ? (
                                    <AddAccessoryButton />
                                ) : (
                                    <PreviewItem
                                        key={`accessory-${idx}-${acc?.product?.id || idx}`}
                                        item={acc}
                                        info={{ ...accessoryInfo, id: `accessories:${idx}` }}
                                        dispatch={dispatch}
                                        onRemove={() => onRemove(`accessories:${idx}`)}
                                        skintone={skintone}
                                        isAccessoryItem
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    const embla = emblaRef.current;

    return (
        <div className="preview-accessories preview-accessories--slider">
            <h3 className="vh">Accessories</h3>
            <div className="preview-accessories__viewport" ref={viewportRef}>
                <div className="preview-accessories__track">
                    {slides.map((group, slideIdx) => (
                        <div className="preview-accessories__slide" key={`accessories-slide-${slideIdx}`}>
                            <div className="preview-accessories__slideInner">
                                {group.map((acc, idx) => {
                                    const absoluteIdx = slideIdx * 4 + idx;
                                    const isPlaceholder = !acc;
                                    return (
                                        <div
                                            className="preview-accessories__gridItem"
                                            key={`accessory-${slideIdx}-${idx}`}
                                        >
                                            {isPlaceholder ? (
                                                <AddAccessoryButton />
                                            ) : (
                                                <PreviewItem
                                                    key={`accessory-${slideIdx}-${idx}-${acc?.product?.id || idx}`}
                                                    item={acc}
                                                    info={{
                                                        ...accessoryInfo,
                                                        id: `accessories:${absoluteIdx}`,
                                                    }}
                                                    dispatch={dispatch}
                                                    onRemove={() => onRemove(`accessories:${absoluteIdx}`)}
                                                    skintone={skintone}
                                                    isAccessoryItem
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {snapCount > 1 && (
                <div className="preview-accessories__pips" aria-label="Accessory pages">
                    {Array.from({ length: snapCount }).map((_, i) => (
                        <button
                            key={`pip-${i}`}
                            type="button"
                            className={`preview-accessories__pip ${i === selectedIndex ? 'is-active' : ''}`}
                            aria-label={`Show accessories page ${i + 1}`}
                            aria-current={i === selectedIndex ? 'true' : undefined}
                            onClick={() => embla && embla.scrollTo(i)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

AccessoriesSlider.propTypes = {
    accessories: PropTypes.arrayOf(PropTypes.object).isRequired,
    accessoryInfo: PropTypes.shape({
        title: PropTypes.string,
        emptyText: PropTypes.string,
        defaultImage: PropTypes.string,
        handle: PropTypes.string,
    }).isRequired,
    dispatch: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    skintone: PropTypes.string,
};

export default AccessoriesSlider;
